/**
 * Read-only view of a `RestoreReport` (the dry-run preview / applied outcome of
 * a revert or snapshot restore).
 *
 * Shows a badge summary (restored / skipped / conflicts) plus a compact table
 * per non-empty bucket via {@link PreviewTable}.
 */

import { Badge, Text } from '@components/base/display';
import { HStack, VStack } from '@components/base/layout';

import {
  entityTypeLabel,
  type RestoreReport,
  type RestoreReportEntry,
} from '../../api/configVersioningService';
import { PreviewTable } from './PreviewTable';

const ACTION_LABELS: Record<string, string> = {
  created: 'Créé',
  updated: 'Modifié',
  unchanged: 'Inchangé',
  deleted: 'Supprimé',
};

function entityLabel(entry: RestoreReportEntry): string {
  return `${entityTypeLabel(entry.entity_type)} · ${entry.entity_key}`;
}

export interface RestoreReportViewProps {
  report: RestoreReport;
}

export function RestoreReportView({ report }: Readonly<RestoreReportViewProps>) {
  const restoredRows = report.restored.map((entry, index) => ({
    id: `restored-${String(index)}`,
    entity: entityLabel(entry),
    outcome: entry.action ? (ACTION_LABELS[entry.action] ?? entry.action) : '—',
  }));

  const skippedRows = report.skipped.map((entry, index) => ({
    id: `skipped-${String(index)}`,
    entity: entityLabel(entry),
    reason: entry.reason ?? '—',
  }));

  const conflictRows = report.conflicts.map((entry, index) => ({
    id: `conflict-${String(index)}`,
    entity: entityLabel(entry),
    reason: entry.reason ?? '—',
  }));

  return (
    <VStack gap={3}>
      <HStack gap={2} wrap>
        <Badge variant="success" size="sm">
          {report.restored.length} restauré{report.restored.length > 1 ? 's' : ''}
        </Badge>
        <Badge variant="warning" size="sm">
          {report.skipped.length} ignoré{report.skipped.length > 1 ? 's' : ''}
        </Badge>
        <Badge variant="error" size="sm">
          {report.conflicts.length} conflit{report.conflicts.length > 1 ? 's' : ''}
        </Badge>
      </HStack>

      {restoredRows.length > 0 && (
        <VStack gap={1}>
          <Text size="sm" weight="semibold">
            Éléments restaurés
          </Text>
          <PreviewTable
            id="config-restore-restored"
            columns={[
              { key: 'entity', label: 'Élément' },
              { key: 'outcome', label: 'Résultat' },
            ]}
            rows={restoredRows}
          />
        </VStack>
      )}

      {skippedRows.length > 0 && (
        <VStack gap={1}>
          <Text size="sm" weight="semibold">
            Éléments ignorés
          </Text>
          <PreviewTable
            id="config-restore-skipped"
            columns={[
              { key: 'entity', label: 'Élément' },
              { key: 'reason', label: 'Raison' },
            ]}
            rows={skippedRows}
          />
        </VStack>
      )}

      {conflictRows.length > 0 && (
        <VStack gap={1}>
          <Text size="sm" weight="semibold">
            Conflits
          </Text>
          <PreviewTable
            id="config-restore-conflicts"
            columns={[
              { key: 'entity', label: 'Élément' },
              { key: 'reason', label: 'Raison' },
            ]}
            rows={conflictRows}
          />
        </VStack>
      )}
    </VStack>
  );
}
